import { defineStore } from 'pinia';
import { formService } from '@/services/formService';

import { useUserStore } from './userStore';
import { useDesignStore } from './designStore';

// import { watch } from 'vue';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { uid } from 'uid';

export const useFormStore = defineStore('form', {
  state: () => ({
    forms: [],
    // Cache of fully-hydrated forms keyed by id. Populated by ensureFormDetails
    // when consumers (e.g. ConditionConfig, FormIntegrationSettingsModal) need
    // the full pages/ending_pages/settings that the slim /forms list endpoint
    // no longer returns.
    formDetailsCache: {},
    formDetailsInflight: {},
    currentForm: {
      id: null,
      name: '',
      pages: [],
      settings: {},
      designId: null,
      enabledIntegrations: [],
      ending_pages: [],
    },
    inputEditing: null,
    activePageIndex: null,
    activeEndingPageIndex: null,
    activeElementIndex: null,
    formIntegrations: {},
    lastSaveTime: null,
    history: [],
    currentHistoryIndex: -1,
    maxHistorySteps: 50,
    isDirty: false,
    isSaving: false,
    isPublishing: false,
    originalFormData: null,
    submissions: [],
    dailySubmissionCounts: [],
    submissionPagination: {
      total: 0,
      page: 1,
      limit: 50,
      hasMore: false,
    },
    submissionsLoading: false,
    showPageSettings: true,
  }),
  getters: {
    pages(state) {
      return state.currentForm ? state.currentForm.pages : [];
    },
    endingPages(state) {
      return state.currentForm ? state.currentForm.ending_pages : [];
    },
    activePage(state) {
      if (state.currentForm && state.activePageIndex !== null) {
        return state.currentForm.pages[state.activePageIndex];
      }
      return null;
    },
    activeElement(state) {
      if (state.currentForm && state.activeElementIndex !== null) {
        if (state.activeEndingPageIndex !== null) {
          // Get element from ending page
          return state.currentForm.ending_pages[state.activeEndingPageIndex].elements?.[
            state.activeElementIndex
          ];
        } else if (state.activePageIndex !== null) {
          // Get element from regular page
          return state.currentForm.pages[state.activePageIndex].elements?.[
            state.activeElementIndex
          ];
        }
      }
      return null;
    },
    activeEndingPage(state) {
      if (state.currentForm && state.activeEndingPageIndex !== null) {
        return state.currentForm.ending_pages[state.activeEndingPageIndex];
      }
      return null;
    },
    // Get all integrations for a specific form
    getFormIntegrations: (state) => (formId) => {
      return state.formIntegrations[formId] || {};
    },
    isIntegrationEnabled: (state) => (integrationId) => {
      return state.currentForm.enabledIntegrations?.includes(integrationId) || false;
    },
    hasDraft: (state) => state.currentForm?.has_draft || false,
    publishedVersion: (state) => state.currentForm?.published_version || 0,
    currentVersion: (state) => state.currentForm?.version || 0,
    hasUnpublishedChanges: (state) => state.currentForm?.has_draft,
    // originalFormData: (state) => state.originalFormData,

    // Configurator specific getters
    isConfigurator: (state) => state.currentForm?.type === 'configurator',
    configuratorSettings: (state) => state.currentForm?.settings?.cpq || {},

    // Get pages that should be visible based on form data (for configurators)
    getVisiblePages:
      (state) =>
      async (formData = {}) => {
        if (state.currentForm?.type !== 'configurator') {
          return state.currentForm?.pages || [];
        }

        // For configurators, generate pages runtime
        const { generateRuntimePages } =
          await import('@shared/composables/useRuntimeConfiguratorGeneration');
        const runtimeGeneration = generateRuntimePages();

        return await runtimeGeneration.generateRuntimePages(state.currentForm, formData);
      },

    // Get next page based on current page and form data
    getNextPage:
      (state) =>
      (currentPageId, formData = {}) => {
        const visiblePages =
          state.currentForm?.type === 'configurator'
            ? state.getters.getVisiblePages(formData)
            : state.currentForm?.pages || [];

        const currentIndex = visiblePages.findIndex((page) => page.id === currentPageId);
        if (currentIndex >= 0 && currentIndex < visiblePages.length - 1) {
          return visiblePages[currentIndex + 1];
        }
        return null;
      },
  },

  actions: {
    async addPage() {
      try {
        const newPage = {
          id: uid(),
          title: `Page ${this.currentForm.pages.length + 1}`,
          elements: [],
        };
        if (this.currentForm) {
          this.currentForm.pages.push(newPage);
        }
        // await this.saveCurrentForm();
        return { status: 'success', message: 'Page added successfully', newPage };
      } catch (error) {
        console.error('Failed to add page:', error);
        return { status: 'error', message: 'Failed to add page', error };
      }
    },

    async removePage(pageIndex) {
      try {
        if (this.currentForm) {
          this.currentForm.pages.splice(pageIndex, 1);
          // Adjust activePageIndex if necessary
          if (pageIndex === this.activePageIndex) {
            this.activePageIndex = this.currentForm.pages.length > 0 ? 0 : null; // Set to the first page or null if no pages left
          } else if (pageIndex < this.activePageIndex) {
            this.activePageIndex--;
          }
          // await this.saveCurrentForm();
          return { status: 'success', message: 'Step removed successfully' };
        }
      } catch (error) {
        console.error('Failed to remove page:', error);
        return { status: 'error', message: 'Failed to remove page', error };
      }
    },

    setActivePage(pageIndex) {
      this.activePageIndex = pageIndex;
      this.activeEndingPageIndex = null;
      this.activeElementIndex = null;
      this.showPageSettings = false;
    },
    setActiveElementIndex(index) {
      this.activeElementIndex = index;
      this.unsetPageSettings();
    },

    setActiveElement(pageIndex = 0, elementIndex = 0) {
      this.activePageIndex = pageIndex;
      this.activeElementIndex = elementIndex;
      this.unsetPageSettings();
    },

    unsetElements() {
      this.activeElementIndex = null;
    },

    updateActiveElementSettings(settings) {
      this.setDirty();
      if (this.activeElement) {
        if (this.activeEndingPageIndex !== null) {
          // Update ending page element
          Object.assign(
            this.currentForm.ending_pages[this.activeEndingPageIndex].elements[
              this.activeElementIndex
            ].props,
            settings
          );
        } else {
          // Update regular page element
          Object.assign(
            this.currentForm.pages[this.activePageIndex].elements[this.activeElementIndex].props,
            settings
          );
        }
        this.triggerUpdateForm(this.currentForm.id);
      }
    },

    editPageSettings(pageIndex) {
      this.inputEditing = {
        type: 'page',
        index: pageIndex,
        settings: { ...this.currentForm.pages[pageIndex] },
      };
    },

    // Update editElementSettings to work with blocks
    editElementSettings(pageIndex, elementIndex) {
      const page = this.currentForm.pages[pageIndex];
      this.inputEditing = {
        type: 'input',
        pageIndex,
        elementIndex,
        settings: { ...page.elements[elementIndex] },
      };
    },
    addElement(element) {
      if (!element.id) {
        element.id = uid();
      }

      if (this.activeEndingPageIndex !== null) {
        // Add to ending page
        if (!this.currentForm.ending_pages[this.activeEndingPageIndex].elements) {
          this.currentForm.ending_pages[this.activeEndingPageIndex].elements = [];
        }
        this.currentForm.ending_pages[this.activeEndingPageIndex].elements.push(element);
        this.activeElementIndex =
          this.currentForm.ending_pages[this.activeEndingPageIndex].elements.length - 1;
      } else if (this.activePageIndex !== null) {
        // Add to regular page
        if (!this.currentForm.pages[this.activePageIndex].elements) {
          this.currentForm.pages[this.activePageIndex].elements = [];
        }
        this.currentForm.pages[this.activePageIndex].elements.push(element);
        this.activeElementIndex = this.currentForm.pages[this.activePageIndex].elements.length - 1;
      }

      this.setDirty();
    },

    removeElement(elementIndex) {
      if (this.activeEndingPageIndex !== null) {
        // Remove from ending page
        this.currentForm.ending_pages[this.activeEndingPageIndex].elements.splice(elementIndex, 1);
      } else {
        // Remove from regular page
        this.currentForm.pages[this.activePageIndex].elements.splice(elementIndex, 1);
      }
      this.activeElementIndex = null;
      this.setDirty();
    },

    cloneElement({ elementIndex }) {
      const sourceElement =
        this.activeEndingPageIndex !== null
          ? this.currentForm.ending_pages[this.activeEndingPageIndex].elements[elementIndex]
          : this.currentForm.pages[this.activePageIndex].elements[elementIndex];

      if (!sourceElement) return;

      const newElement = {
        ...sourceElement,
        id: uid(),
        props: { ...sourceElement.props },
      };

      if (this.activeEndingPageIndex !== null) {
        // Add to ending page
        this.currentForm.ending_pages[this.activeEndingPageIndex].elements.push(newElement);
        this.activeElementIndex =
          this.currentForm.ending_pages[this.activeEndingPageIndex].elements.length - 1;
      } else {
        // Add to regular page
        this.currentForm.pages[this.activePageIndex].elements.push(newElement);
        this.activeElementIndex = this.currentForm.pages[this.activePageIndex].elements.length - 1;
      }

      this.setDirty();
    },

    getAllElements() {
      return this.currentForm?.pages.flatMap((page) =>
        page.elements.map((element) => ({
          id: element.id,
          props: element.props,
          type: element.type,
          pageId: page.id,
        }))
      );
    },

    saveSettings(settings) {
      if (this.inputEditing.type === 'page') {
        const page = this.currentForm.pages[this.inputEditing.index];
        Object.assign(page, settings);
      } else if (this.inputEditing.type === 'input') {
        const element =
          this.currentForm.pages[this.inputEditing.pageIndex].elements[
            this.inputEditing.elementIndex
          ];
        Object.assign(element, settings);
      }
      this.inputEditing = null;
    },

    async updatePage(pageIndex, data) {
      if (this.currentForm && this.currentForm.pages[pageIndex]) {
        // First merge the data and the edited page settings
        Object.assign(this.currentForm.pages[pageIndex], data);
      }
      // Then save the form
      await this.saveCurrentForm();
    },

    async publishChanges(formId) {
      this.isPublishing = true;
      try {
        const response = await formService.publishForm(formId);
        this.currentForm = {
          ...response,
          has_draft: false,
        };
        this.isDirty = false;
        return response;
      } catch (error) {
        console.error('Failed to publish form:', error);
        throw error;
      } finally {
        this.isPublishing = false;
      }
    },

    async unpublishForm(formId) {
      this.isPublishing = true;
      try {
        const response = await formService.unpublishForm(formId);
        this.currentForm = {
          ...response,
          has_draft: true,
        };
        this.isDirty = false;
        return response;
      } catch (error) {
        console.error('Failed to unpublish form:', error);
        throw error;
      } finally {
        this.isPublishing = false;
      }
    },

    async saveCurrentForm() {
      this.isSaving = true;
      try {
        // Create a clean copy of the form data
        const form = {
          id: this.currentForm.id,
          name: this.currentForm.name,
          pages: this.currentForm.pages,
          ending_pages: this.currentForm.ending_pages,
          settings: this.currentForm.settings,
          design_id: this.currentForm.design_id,
          enabledIntegrations: this.currentForm.enabledIntegrations,
          version: this.currentForm.version,
          published_version: this.currentForm.published_version,
          has_draft: true,
        };

        const updatedForm = await formService.updateForm(this.currentForm.id, form);

        // Preserve the current form's state while updating with server response
        this.currentForm = {
          ...updatedForm,
          ending_pages: this.currentForm.ending_pages,
          has_draft: true,
          published_version: this.currentForm.published_version,
          published_at: this.currentForm.published_at,
        };

        // Create a clean copy for comparison after saving
        const cleanCopy = {
          id: this.currentForm.id,
          name: this.currentForm.name,
          pages: this.currentForm.pages,
          ending_pages: this.currentForm.ending_pages,
          settings: this.currentForm.settings,
          design_id: this.currentForm.design_id,
          enabledIntegrations: this.currentForm.enabledIntegrations,
        };

        // Update the original data after successful save
        this.originalFormData = JSON.stringify(cleanCopy);
        this.lastSaveTime = new Date();
        this.isDirty = false;

        // Add to history
        this.saveToHistory();

        return updatedForm;
      } catch (error) {
        console.error('Failed to save form:', error);
        throw error;
      } finally {
        this.isSaving = false;
      }
    },

    saveToHistory() {
      // Remove any future states if we're in the middle of the history
      if (this.currentHistoryIndex < this.history.length - 1) {
        this.history = this.history.slice(0, this.currentHistoryIndex + 1);
      }

      // Add current state to history
      this.history.push(JSON.stringify(this.currentForm));

      // Remove oldest entries if exceeding max size
      if (this.history.length > this.maxHistorySteps) {
        this.history.shift();
      } else {
        this.currentHistoryIndex++;
      }
    },

    undo() {
      if (this.currentHistoryIndex > 0) {
        this.currentHistoryIndex--;
        this.currentForm = JSON.parse(this.history[this.currentHistoryIndex]);
        this.setDirty();
      }
    },

    redo() {
      if (this.currentHistoryIndex < this.history.length - 1) {
        this.currentHistoryIndex++;
        this.currentForm = JSON.parse(this.history[this.currentHistoryIndex]);
        this.setDirty();
      }
    },

    async loadForm(id) {
      try {
        const response = await formService.getFormById(id);
        this.currentForm = response;

        // Ensure ending_pages exists and has at least one default ending page
        // if (!this.currentForm.ending_pages) {
        //   this.currentForm.ending_pages = [];
        // }

        // if (this.currentForm.ending_pages.length === 0) {
        //   this.currentForm.ending_pages.push({
        //     id: crypto.randomUUID(),
        //     title: 'Success',
        //     type: 'success',
        //     content: 'Thank you for submitting the form!',
        //   });
        // }

        // Create a clean copy for comparison
        const cleanCopy = {
          id: this.currentForm.id,
          name: this.currentForm.name,
          pages: this.currentForm.pages,
          ending_pages: this.currentForm.ending_pages,
          settings: this.currentForm.settings,
          designId: this.currentForm.designId,
          enabledIntegrations: this.currentForm.enabledIntegrations,
        };

        this.originalFormData = JSON.stringify(cleanCopy);

        return response;
      } catch (error) {
        console.error('Failed to load form:', error);
        throw error;
      }
    },

    setDirty() {
      this.isDirty = true;
    },

    // Instead of a simple setDirty, compare with original data
    checkIfDirty(currentFormData) {
      if (!this.originalFormData) return false;

      try {
        const originalData = JSON.parse(this.originalFormData);

        // Compare relevant fields only (excluding metadata like updated_at)
        const relevantFields = ['name', 'pages', 'ending_pages', 'settings'];

        // Deep comparison of each field
        return relevantFields.some((field) => {
          const currentValue = currentFormData[field];
          const originalValue = originalData[field];

          // Handle arrays (pages and ending_pages)
          if (Array.isArray(currentValue)) {
            return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
          }

          // Handle objects (settings)
          if (typeof currentValue === 'object' && currentValue !== null) {
            return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
          }

          // Handle primitive values (name)
          return currentValue !== originalValue;
        });
      } catch (error) {
        console.error('Error checking if form is dirty:', error);
        return true; // If we can't compare, assume it's dirty to be safe
      }
    },

    async fetchForms(options = {}) {
      const { force = false } = options;

      // Skip fetch if forms are already loaded (unless force refresh)
      if (!force && this.forms.length > 0) {
        return this.forms;
      }

      // De-duplicate concurrent calls. Without this, a page's onMounted
      // fetchForms() racing with the background prefetch in userStore would
      // hit /forms twice in parallel (cache-check passes for both because
      // neither has resolved yet).
      if (!force && this._fetchFormsInflight) {
        return this._fetchFormsInflight;
      }

      const promise = (async () => {
        try {
          const forms = await formService.getForms();
          this.forms = forms;
          return forms;
        } catch (error) {
          console.error('Failed to fetch forms:', error);
          throw error;
        } finally {
          this._fetchFormsInflight = null;
        }
      })();

      this._fetchFormsInflight = promise;
      return promise;
    },

    async fetchFormById(id) {
      const form = await formService.getFormById(id);
      return form;
    },

    /**
     * Returns the fully-hydrated form (with pages/ending_pages/settings) for
     * the given id, fetching it once and caching it. The list endpoint
     * (`fetchForms`) only returns summary fields, so consumers that need the
     * full structure must go through this action instead of reading from
     * `state.forms`.
     *
     * @param {string} id
     * @param {{ force?: boolean }} [options]
     * @returns {Promise<object|null>}
     */
    async ensureFormDetails(id, { force = false } = {}) {
      if (!id) return null;

      if (!force && this.formDetailsCache[id]) {
        return this.formDetailsCache[id];
      }

      // De-duplicate concurrent calls for the same id.
      if (this.formDetailsInflight[id]) {
        return this.formDetailsInflight[id];
      }

      const promise = (async () => {
        try {
          const form = await formService.getFormById(id);
          if (form) {
            this.formDetailsCache = { ...this.formDetailsCache, [id]: form };
          }
          return form;
        } finally {
          delete this.formDetailsInflight[id];
        }
      })();

      this.formDetailsInflight[id] = promise;
      return promise;
    },

    invalidateFormDetails(id) {
      if (!id) return;
      const next = { ...this.formDetailsCache };
      delete next[id];
      this.formDetailsCache = next;
    },

    async createForm(formData) {
      try {
        const userStore = useUserStore();
        const data = {
          ...formData,
          uid: userStore.user.id,
        };

        // For configurators, ensure type is set
        if (formData.settings?.type === 'configurator') {
          data.type = 'configurator';
        }

        const response = await formService.createForm(data);
        this.forms.push(response.data);
        return response;
      } catch (error) {
        console.error('Failed to create form:', error);
        throw error;
      }
    },

    async createFormWithAI(data) {
      try {
        const userStore = useUserStore();
        const formData = {
          ...data,
          uid: userStore.user.id,
          organizationId: userStore.currentOrganization.id,
        };
        const response = await formService.createFormWithAI(formData);
        this.forms.push(response.data);
        return response;
      } catch (error) {
        console.error('Failed to create form with AI:', error);
        throw error;
      }
    },

    async deleteForm(id) {
      try {
        const deletedForm = await formService.deleteForm(id);
        if (deletedForm.status === 204) {
          this.forms = this.forms.filter((form) => form.id !== id);
          this.invalidateFormDetails(id);
        }
      } catch (error) {
        console.error('Error deleting form:', error);
        // The caller shows a message for this one; other errors stay silent as before
        if (error.response?.data?.code === 'FORM_HAS_BOOKINGS') throw error;
      }
    },

    // Debounced update function
    debouncedUpdateForm: debounce(async function (id, form) {
      try {
        await formService.updateForm(id, form);
        this.lastSaveTime = new Date();
      } catch (error) {
        console.error('Error updating form:', error);
      }
    }, 30000), // 30 second debounce

    // Function to trigger the debounced update
    triggerUpdateForm(id) {
      const form = {
        name: this.currentForm.name,
        pages: this.pages,
        ending_pages: this.currentForm.ending_pages,
        settings: this.currentForm.settings,
      };
      this.saveToHistory();
      this.debouncedUpdateForm(id, form);
    },

    updateElementProps(pageIndex, elementIndex, settings) {
      Object.assign(this.currentForm.pages[pageIndex].elements[elementIndex].props, settings);
      this.triggerUpdateForm(this.currentForm.id);
    },
    async setFormDesign(designId) {
      if (this.currentForm) {
        this.currentForm.design_id = designId;
        const designStore = useDesignStore();
        await designStore.loadDesign(designId);
        await this.saveCurrentForm();
      }
    },

    async updateFormName(name) {
      this.currentForm.name = name;
      await this.saveCurrentForm();
    },

    async updateFormSettings(formId, settings, options = {}) {
      try {
        const { merge = true } = options;

        // Always merge on frontend side to ensure we don't lose data
        // This protects against backend implementations that might replace completely
        const settingsToSend = merge ? { ...this.currentForm.settings, ...settings } : settings;

        const response = await axios.patch(`/forms/${formId}/settings`, {
          settings: settingsToSend,
          merge: merge, // Inform backend about merge intention
        });

        // Update local state with response from backend
        this.currentForm.settings = response.data.settings;

        return response.data;
      } catch (error) {
        console.error('❌ updateFormSettings error:', error);
        throw new Error('Failed to update form settings');
      }
    },

    // Specific method for CPQ settings
    async updateCPQSettings(formId, cpqSettings) {
      return this.updateFormSettings(formId, { cpq: cpqSettings });
    },

    // Specific method for general form settings (appearance, behavior, etc.)
    async updateGeneralSettings(formId, generalSettings) {
      return this.updateFormSettings(formId, generalSettings);
    },

    async getFormSubmissions(formId, page = 1, limit = 50, status = 'all') {
      try {
        this.submissionsLoading = true;
        const response = await axios.get(`/submissions`, {
          params: {
            formId,
            page,
            limit,
            status,
          },
        });
        this.$patch((state) => {
          state.submissions = response.data.submissions;
          state.submissionPagination = {
            total: response.data.pagination.total,
            page: response.data.pagination.page,
            limit: response.data.pagination.limit,
            hasMore: response.data.pagination.hasMore,
          };
          state.dailySubmissionCounts = response.data.dailySubmissionCounts;
        });
        return response.data;
      } catch (error) {
        console.log(error);
        throw new Error('Failed to get form submissions');
      } finally {
        this.submissionsLoading = false;
      }
    },

    async getFormSubmission(submissionId) {
      try {
        const response = await axios.get(`/submissions/${submissionId}`);
        return response.data;
      } catch (error) {
        console.log(error);
        throw new Error('Failed to get form submissions');
      }
    },

    async updateSubmissionStatus(submissionId, newStatus) {
      try {
        const response = await axios.patch(`/submissions/${submissionId}/status`, {
          status: newStatus,
        });
        return response.data;
      } catch (error) {
        console.log(error);
        throw new Error('Failed to update submission status');
      }
    },

    async toggleIntegration(integrationId) {
      try {
        if (!this.currentForm.enabledIntegrations) {
          this.currentForm.enabledIntegrations = [];
        }

        const isEnabled = this.isIntegrationEnabled(integrationId);

        // Toggle the integration
        if (isEnabled) {
          this.currentForm.enabledIntegrations = this.currentForm.enabledIntegrations.filter(
            (id) => id !== integrationId
          );
        } else {
          this.currentForm.enabledIntegrations.push(integrationId);
        }

        // Save the form
        // await this.saveCurrentForm();
      } catch (error) {
        console.error('Failed to toggle integration:', error);
        throw error;
      }
    },

    async fetchFormIntegrations(formId) {
      try {
        const response = await axios.get(`/forms/${formId}/integrations`);

        // Update local state
        if (!this.formIntegrations[formId]) {
          this.formIntegrations[formId] = {};
        }
        this.formIntegrations[formId] = response.data.reduce((acc, integration) => {
          acc[integration.integrationId] = {
            enabled: integration.enabled,
            settings: integration.settings || {},
          };
          return acc;
        }, {});

        return response.data;
      } catch (error) {
        console.error('Failed to fetch form integrations:', error);
        throw error;
      }
    },

    async updatePageSettings({ id, logic }) {
      try {
        const pageIndex = this.currentForm.pages.findIndex((page) => page.id === id);
        if (pageIndex === -1) throw new Error('Page not found');

        // Update the page logic
        this.currentForm.pages[pageIndex].logic = logic;

        // Save the form
        await this.saveCurrentForm();

        return { status: 'success' };
      } catch (error) {
        console.error('Failed to update page settings:', error);
        return { status: 'error', error };
      }
    },

    setShowPageSettings(value) {
      this.showPageSettings = value;
      this.activeElementIndex = null;
    },

    unsetPageSettings() {
      this.showPageSettings = false;
    },

    async addEndingPage(endingPage = null) {
      try {
        const defaultEndingPage = {
          id: uid(),
          title: 'Ending',
          elements: [
            {
              id: uid(),
              type: 'thank-you',
              label: 'Thank You',
              props: {
                icon: 'check-circle',
                label: 'Thank You',
                message: 'Thank you for your submission!',
                iconSize: '48',
                conditions: {
                  type: 'show',
                  groups: [],
                  betweenGroupLogic: 'AND',
                },
              },
            },
          ],
        };

        const newEndingPage = endingPage || defaultEndingPage;

        if (!this.currentForm.ending_pages) {
          this.currentForm.ending_pages = [];
        }

        this.currentForm.ending_pages.push(newEndingPage);
        await this.saveCurrentForm();

        return { status: 'success', endingPage: newEndingPage };
      } catch (error) {
        console.error('Failed to add ending page:', error);
        return { status: 'error', error };
      }
    },

    removeEndingPage(pageIndex) {
      if (this.currentForm.ending_pages.length <= 1) {
        return { status: 'error', message: 'You must have at least one ending page in your form.' };
      }
      this.setDirty();
      this.currentForm.ending_pages.splice(pageIndex, 1);
      if (this.activeEndingPageIndex === pageIndex) {
        this.setActiveEndingPage(Math.max(0, pageIndex - 1));
      }
      return { status: 'success' };
    },

    setActiveEndingPage(pageIndex) {
      this.activeEndingPageIndex = pageIndex;
      this.activePageIndex = null;
      this.activeElementIndex = null;
      this.showPageSettings = false;
    },

    async updateEndingPage(pageIndex, data) {
      try {
        if (this.currentForm && this.currentForm.ending_pages) {
          const endingPage = this.currentForm.ending_pages[pageIndex];
          if (endingPage) {
            Object.assign(endingPage, data);
            this.setDirty();
            await this.saveCurrentForm();
            return { status: 'success', message: 'Ending page updated successfully' };
          }
        }
        throw new Error('Ending page not found');
      } catch (error) {
        console.error('Failed to update ending page:', error);
        return { status: 'error', message: 'Failed to update ending page', error };
      }
    },
  },
});

// // Watch for changes in the currentForm and trigger the debounced update
// const formStore = useFormStore();
// watch(() => formStore.currentForm, (newForm) => {
//   if (newForm.id) {
//     formStore.triggerUpdateForm(newForm.id);
//   }
// }, { deep: true });
